import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import * as _ from 'lodash';
import axiosClient from '../../http/axios-client';
import { useQueryParams } from './query-params-hook';
import { usePrevious } from '../utils';

export const useItems = (collectionName, options = {}, extra = {}) => {
  const defaultOptions = {
    queryOptions: {
      enabled: true,
    },
    invalidationKeys: [],
    filterQuery: {},
  };
  const opts = _.merge(defaultOptions, options);

  const {
    page,
    setPage,
    limit,
    setLimit,
    sort,
    setSort,
  } = useQueryParams();

  const prevFilterQuery = usePrevious(opts.filterQuery);
  useEffect(() => {
    if (!_.isEqual(prevFilterQuery, opts.filterQuery)) {
      setPage(0);
    }
  }, [ opts ]);

  const makeAxiosParams = (
    {
      page: p,
      limit: l,
      sort: s,
    },
  ) => {
    const _limit = l;
    const _start = _limit * p;
    const _sort = `${ s.orderBy }:${ s.order.toUpperCase() }`;

    return {
      _start,
      _limit,
      _sort,
    };
  };

  const endpoint = `${ collectionName }`;

  const invalidationKeys = [
    `${ collectionName }`,
    ...opts.invalidationKeys,
  ];

  const queryClient = useQueryClient();

  const invalidateKeys = () => {
    const invalidationPromises = invalidationKeys.map((k) => {
      return queryClient.invalidateQueries([ k ]);
    });

    return Promise.all(invalidationPromises);
  };

  const {
    data: items,
    isLoading: itemsLoading,
    error: itemsError,
    refetch,
    isFetching: itemsFetching,
  } = useQuery(
    [ `${ collectionName }`, { page, limit, sort, ...opts.filterQuery }],
    () => {
      return axiosClient.get(endpoint, {
        params: {
          ...makeAxiosParams({ page, limit, sort }),
          ...opts.filterQuery,
        },
      });
    },
    {
      ...opts.queryOptions,
      keepPreviousData: true,
    },
  );

  const {
    data: count,
    isLoading: countIsLoading,
  } = useQuery(
    [ `${ collectionName }`, 'COUNT', extra ],
    () => {
      return axiosClient.get(`${ endpoint }/count`, {
        params: {
          _limit: -1,
          ...extra,
        },
      });
    },
    {
      ...opts.queryOptions,
    },
  );

  const {
    data: countFiltered,
    isLoading: countFilteredIsLoading,
  } = useQuery(
    [ `${ collectionName }`, 'COUNT', { ...opts.filterQuery }],
    () => {
      return axiosClient.get(`${ endpoint }/count`, {
        params: {
          _limit: -1,
          ...opts.filterQuery,
        },
      });
    },
    {
      ...opts.queryOptions,
    },
  );

  // Prefetch the next page!
  useEffect(() => {
    if ([ countFiltered, page, limit ].some((metric) => { return 'number' !== typeof metric; })) {
      return;
    }
    const nextPage = page + 1;
    const hasMore = countFiltered > nextPage * limit;
    if (hasMore) {
      queryClient.prefetchQuery(
        [ `${ collectionName }`, { page: nextPage, limit, sort, ...opts.filterQuery }],
        () => {
          return axiosClient.get(endpoint, {
            params: {
              ...makeAxiosParams({ page: nextPage, limit, sort }),
              ...opts.filterQuery,
            },
          });
        },
      );
    }
  }, [ countFiltered, page, limit, queryClient ]);

  // fix page
  useEffect(() => {
    if ([ countFiltered, page, limit ].some((metric) => { return 'number' !== typeof metric; })) {
      return;
    }
    const lastPossiblePage = Math.floor(Math.max(0, countFiltered - 1) / limit);

    if (lastPossiblePage < page) {
      setPage(lastPossiblePage);
    }
  }, [ countFiltered, page, limit ]);

  const {
    mutateAsync: createItem,
    isLoading: itemIsCreating,
  } = useMutation(
    [ `${ collectionName }` ],
    (payload) => {
      return axiosClient.post(endpoint, payload);
    },
    {
      onSettled() {
        return invalidateKeys();
      },
    },
  );

  const {
    mutateAsync: deleteItem,
  } = useMutation(
    [ `${ collectionName }`, 'DELETE' ],
    (id) => {
      return axiosClient.delete(`${ endpoint }/${ id }`);
    },
    {
      onSettled() {
        return invalidateKeys();
      },
    },
  );

  return {
    items,
    itemsLoading,
    itemsError,
    count,
    countIsLoading,
    countFiltered,
    countFilteredIsLoading,
    createItem,
    deleteItem,
    refetch,
    itemIsCreating,
    itemsFetching,
    pagination: {
      page,
      setPage,
      limit,
      setLimit,
      sort,
      setSort,
      count: countFiltered,
    },
  };
};
